import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./PrivacyInfo.css";

const PrivacyInfo = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (id) =>
    setExpandedSection((prev) => (prev === id ? null : id));

  const privacySections = [
    {
      id: "collection",
      title: "Information We Collect",
      icon: "📊",
      content: {
        description:
          "We collect information necessary to provide cybersecurity monitoring and advisory services.",
        details: [
          "Domain and subdomain information for monitoring",
          "Publicly available DNS and SSL certificate data",
          "Email addresses found in public breach databases",
          "Brand mentions and digital asset information",
          "Technical metadata from authorized security scans",
        ],
      },
    },
    {
      id: "usage",
      title: "How We Use Information",
      icon: "🔍",
      content: {
        description:
          "Information is used exclusively for cybersecurity monitoring and threat intelligence.",
        details: [
          "External threat surface monitoring and assessment",
          "Brand protection and impersonation detection",
          "Vulnerability identification and reporting",
          "Threat intelligence analysis and alerting",
          "Expert advisory and consultation services",
        ],
      },
    },
    {
      id: "sources",
      title: "Data Sources",
      icon: "🌐",
      content: {
        description:
          "We collect information from legitimate, publicly available sources.",
        details: [
          "Public DNS records and WHOIS databases",
          "Certificate transparency logs",
          "Public breach databases and security feeds",
          "Social media platforms (public content only)",
          "Search engines and web crawling (authorized domains)",
        ],
      },
    },
    {
      id: "protection",
      title: "Data Protection",
      icon: "🛡️",
      content: {
        description:
          "We implement comprehensive security measures to protect all information.",
        details: [
          "End-to-end encryption for data transmission",
          "Secure cloud infrastructure with access controls",
          "Regular security audits and vulnerability assessments",
          "Employee security training and background checks",
          "Incident response procedures for data protection",
        ],
      },
    },
  ];

  return (
    <div className="row clearfix">
      <div className="col-sm-2"></div>
      <div className="col-sm-9" style={{ padding: 0 }}>
        <div className="container-fluid">
          {/* Header / breadcrumb */}
          <div className="block-header">
            <div className="row clearfix">
              <div className="col-md-6 col-sm-12">
                <h2>Privacy Information</h2>
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/">Home</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Privacy Information
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>

          {/* Intro card */}
          <div className="row clearfix">
            <div className="col-xl-12">
              <div className="card pv-card">
                <div className="header">
                  <h2>Privacy Policy</h2>
                  <p className="text-muted">
                    Last updated: September 2024 | Effective Date: September 1, 2024
                  </p>
                  <hr />
                  <p className="pv-intro">
                    At iOdin, we are committed to protecting your privacy while
                    providing comprehensive cybersecurity monitoring and advisory
                    services. This privacy policy explains how we collect, use, and
                    protect information in the course of our external threat surface
                    management services.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Accordions */}
          <div className="row clearfix">
            {privacySections.map((section) => {
              const isOpen = expandedSection === section.id;

              // Auto-height panel using a ref (no clipping)
              const Panel = () => {
                const ref = useRef(null);
                const [maxH, setMaxH] = useState(0);

                useEffect(() => {
                  if (isOpen && ref.current) setMaxH(ref.current.scrollHeight);
                  else setMaxH(0);
                }, [isOpen]);

                useEffect(() => {
                  const onResize = () => {
                    if (isOpen && ref.current) {
                      setMaxH(ref.current.scrollHeight);
                    }
                  };
                  window.addEventListener("resize", onResize);
                  return () => window.removeEventListener("resize", onResize);
                }, [isOpen]);

                return (
                  <div
                    id={`panel-${section.id}`}
                    className={`pv-accordion-panel ${isOpen ? "open" : ""}`}
                    style={{ maxHeight: maxH }}
                    aria-hidden={!isOpen}
                  >
                    <div ref={ref} className="body pv-accordion-inner">
                      <p className="text-muted mb-3">
                        {section.content.description}
                      </p>
                      <ul className="list-unstyled">
                        {section.content.details.map((detail, i) => (
                          <li key={i} className="mb-2">
                            <i className="fa fa-check text-success mr-2" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              };

              return (
                <div key={section.id} className="col-xl-6 col-lg-6 col-md-12 mb-4">
                  <div className="card pv-card">
                    <div className="header">
                      <button
                        type="button"
                        className={`pv-accordion-btn ${isOpen ? "is-open" : ""}`}
                        onClick={() => toggleSection(section.id)}
                        aria-expanded={isOpen}
                        aria-controls={`panel-${section.id}`}
                      >
                        <span className="mr-2">{section.icon}</span>
                        <span className="pv-accordion-title">{section.title}</span>
                        <span className="pv-chevron" aria-hidden="true">
                          <i className="fa fa-chevron-down" />
                        </span>
                      </button>
                    </div>

                    <Panel />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Contact card */}
          <div className="row clearfix">
            <div className="col-xl-12">
              <div className="card pv-card">
                <div className="header">
                  <h2>Contact Us About Privacy</h2>
                </div>
                <div className="body">
                  <div className="row">
                    <div className="col-md-6">
                      <h5>Privacy Officer</h5>
                      <p>
                        <strong>Email:</strong> privacy@iodin.com
                        <br />
                        <strong>Phone:</strong> 1234567
                        <br />
                        <strong>Address:</strong> Oslo, Norway
                        
                      </p>
                    </div>

                    <div className="col-md-6">
                      <h5>Privacy Requests</h5>
                      <p>
                        For privacy-related inquiries, data access requests, or to
                        exercise your privacy rights, please contact our privacy team.
                        We will respond to all requests within 30 days as required by
                        applicable regulations.
                      </p>
                      <Link to="/contact" className="btn btn-primary btn-round">
                        Submit Privacy Request
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* /Contact card */}
        </div>
      </div>
    </div>
  );
};

export default PrivacyInfo;
